import React, { useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Table from '../common/Table';
import ConfirmationModal from '../common/ConfirmationModal';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useFetch, useSubmission } from '../../hooks/useCallApi';
import { natureOfBusinessSchema } from '../../validation/businessPermit/businessRegistrationSchema';

const NewNatureOfBusiness = ({ isOpen, onClose }) => {
  const [natureName, setNatureName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [editingNatureOfBusiness, setEditingNatureOfBusiness] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const queryClient = useQueryClient();

  // API params for fetching nature of business
  const params = useMemo(() => ({
    per_page: perPage,
    page: currentPage,
  }), [perPage, currentPage]);

  // Fetch nature of business
  const {
    data: natureOfBusinessData,
    isLoading,
  } = useFetch(
    ['nature-of-business', params],
    (api) => api.admin.businessPermitsAPI.getAllNatureOfBusiness(params),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
      onError: () => {
        console.error('Failed to fetch nature of business');
      },
    }
  );

  // Normalize API response
  const normalizeApiListResponse = (response) => {
    return (
      response?.data?.businesse_natures || response?.data?.business_natures ||
      (Array.isArray(response?.data) ? response.data : null) ||
      (Array.isArray(response) ? response : []) ||
      []
    );
  };

  const natureOfBusinessList = normalizeApiListResponse(natureOfBusinessData);

  // Pagination — server-side meta
  const totalPages = natureOfBusinessData?.data?.meta?.last_page || 1;
  const totalItems = natureOfBusinessData?.data?.meta?.total || 0;
  const currentPageFromServer = natureOfBusinessData?.data?.meta?.current_page || currentPage;

  // Create nature of business mutation
  const { mutateAsync: createNatureOfBusiness, isLoading: isCreating } = useSubmission(
    ['create-nature-of-business'],
    (api, data) => api.admin.businessPermitsAPI.createNatureOfBusiness(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['nature-of-business']);
        setNatureName('');
        setShortDescription('');
      },
    }
  );

  // Update nature of business mutation
  const { mutateAsync: updateNatureOfBusiness, isLoading: isUpdating } = useSubmission(
    ['update-nature-of-business'],
    (api, { id, data }) => api.admin.businessPermitsAPI.updateNatureOfBusiness(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['nature-of-business']);
        setEditingNatureOfBusiness(null);
        setIsEditModalOpen(false);
      },
    }
  );

  // Archive nature of business mutation
  const { mutateAsync: archiveNatureOfBusiness } = useSubmission(
    ['archive-nature-of-business'],
    (api, id) => api.admin.businessPermitsAPI.archiveNatureOfBusiness(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['nature-of-business']);
      },
    }
  );

  const handleAddNatureOfBusiness = async () => {
    try {
      const formData = {
        name: natureName.trim(),
        description: shortDescription.trim(),
      };

      await natureOfBusinessSchema.validate(formData, { abortEarly: false });

      await createNatureOfBusiness(formData);
      toast.success('Nature of business added successfully.');
    } catch (err) {
      if (err.name === 'ValidationError') {
        err.errors.forEach((error) => toast.error(error));
      } else {
        console.error('Failed to create nature of business:', err);
        toast.error('Failed to add nature of business. Please try again.');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingNatureOfBusiness(item);
    setNatureName(item.name || '');
    setShortDescription(item.description || '');
    setIsEditModalOpen(true);
  };

  const handleUpdateNatureOfBusiness = async () => {
    if (!editingNatureOfBusiness) return;

    try {
      const formData = {
        name: natureName.trim(),
        description: shortDescription.trim(),
      };

      await businessTypeSchema.validate(formData, { abortEarly: false });

      await updateNatureOfBusiness({
        id: editingNatureOfBusiness.id,
        data: formData,
      });
      setNatureName('');
      setShortDescription('');
      toast.success('Nature of business updated successfully.');
    } catch (err) {
      if (err.name === 'ValidationError') {
        err.errors.forEach((error) => toast.error(error));
      } else {
        console.error('Failed to update nature of business:', err);
        toast.error('Failed to update nature of business. Please try again.');
      }
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await archiveNatureOfBusiness(itemToDelete.id);
      toast.success(`"${itemToDelete.name}" has been archived.`);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Failed to archive nature of business:', err);
      toast.error('Failed to archive nature of business. Please try again.');
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'name',
      label: 'NAME',
      render: (value) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'description',
      label: 'SHORT DESCRIPTION',
      render: (value) => <span className="text-gray-600">{value}</span>,
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      render: (value, row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Archive"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Nature of Business"
      size="4xl"
    >
      <div className="space-y-6">
        {/* Add New Nature of Business Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Nature of Business</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={natureName}
                onChange={(e) => setNatureName(e.target.value)}
                placeholder="Enter nature of business"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <input
                type="text"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Enter short description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={handleAddNatureOfBusiness}
              variant="primary"
              className="px-4 py-2"
              disabled={isCreating}
            >
              {isCreating ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>

        {/* Table Section */}
        <div>
          <Table
            columns={columns}
            data={natureOfBusinessList}
            pagination={{
              currentPage: currentPageFromServer,
              lastPage: totalPages,
              perPage: perPage,
              total: totalItems,
            }}
            onPageChange={handlePageChange}
            paginationProps={{
              onPerPageChange: handlePerPageChange,
              isLoading,
            }}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingNatureOfBusiness(null);
            setNatureName('');
            setShortDescription('');
          }}
          title="Edit Nature of Business"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={natureName}
                onChange={(e) => setNatureName(e.target.value)}
                placeholder="Enter nature of business"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <input
                type="text"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Enter short description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingNatureOfBusiness(null);
                  setNatureName('');
                  setShortDescription('');
                }}
                variant="secondary"
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateNatureOfBusiness}
                variant="primary"
                className="px-4 py-2"
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Archive Nature of Business"
        message={`Are you sure you want to archive "${itemToDelete?.name || 'this nature of business'}"? This action can be reversed.`}
        confirmText="Archive"
        cancelText="Cancel"
        isLoading={false}
      />
    </Modal>
  );
};

export default NewNatureOfBusiness;